/**
 * nav-registry — the single source of truth for navigation metadata.
 *
 * Sidebar groups, the command palette, breadcrumbs, document titles, the
 * journey trail, and the project-context lookups all derive from this table.
 * Before this file existed the same facts were hand-copied across four
 * modules (breadcrumbs LABELS/DETAIL_ROUTES, nav-context NAV_LABELS/
 * DETAIL_SECTIONS, app-shell PROJECT_SCOPED_DETAILS, the palette's item
 * list) and had already drifted apart — a wrong query key silently killed
 * off-page-brief breadcrumbs, competitor-analyses was missing everywhere,
 * and `/tracking` rendered a lowercase crumb. Keeping one table makes that
 * class of bug structural rather than vigilance-based.
 *
 * React-free on purpose (icons are passed as component references) so it can
 * be unit-tested without a DOM.
 */
import {
  BarChart3,
  CalendarDays,
  CreditCard,
  FileSearch,
  FolderKanban,
  Globe,
  KeyRound,
  LineChart,
  Megaphone,
  Notebook,
  Plug,
  Radar,
  ScanSearch,
  Target,
  TrendingUp,
  UserCircle,
  Users,
  type LucideIcon,
} from 'lucide-react';

export const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Only ever trust a `next`/return-to value as an internal path — an absolute
 * URL here would be an open-redirect hole. Anything not starting with a
 * single `/` is dropped.
 */
export function sanitizeNextPath(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  if (!v.startsWith('/') || v.startsWith('//')) return undefined;
  return v;
}

/** Loose entity shape as it sits in the React Query cache. */
export type CachedEntity = Record<string, unknown>;

export interface DetailDef {
  /** Router path pattern, e.g. `/audits/$auditId`. */
  routePath: string;
  /** Param name in `routePath`. */
  paramName: string;
  /** Query key the section's `useX(id)` hook caches the entity under. */
  queryKey: (id: string) => readonly unknown[];
  /** Human label for a cached entity; null → caller falls back to `…uuid6`. */
  entityLabel: (entity: CachedEntity) => string | null;
  /** Hash of the owning tab on the project detail page. */
  projectTab?: string;
}

export type SectionGroup = 'workspace' | 'research' | 'content' | 'strategy';

export interface SectionDef {
  /** Top-level route path, e.g. `/audits`. Also the sidebar link target. */
  path: string;
  /** Full display label (sidebar, palette). */
  label: string;
  /** Shorter label for breadcrumbs; defaults to `label`. */
  crumbLabel?: string;
  icon: LucideIcon;
  /** Plain-English meaning — sidebar tooltips + palette hint. */
  description?: string;
  /** Extra palette search terms. */
  keywords?: string[];
  group: SectionGroup;
  /** Tenant feature flag gating this section (sidebar + palette). */
  featureFlag?: string;
  /** False → not shown in the sidebar (still valid for crumbs/palette). */
  sidebar?: boolean;
  detail?: DetailDef;
}

const shortDate = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = new Date(v);
  return Number.isNaN(t.getTime())
    ? null
    : t.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim().length > 0 ? v : null;

/** Mirrors the seed-label logic used across keyword pages. */
export function keywordSeedLabel(seed: unknown): string | null {
  if (!seed || typeof seed !== 'object') return null;
  const s = seed as { type?: string; value?: unknown };
  if (s.type === 'domain' && typeof s.value === 'string') return s.value;
  if (s.type === 'topics' && Array.isArray(s.value)) {
    const head = s.value.slice(0, 3).join(', ');
    return s.value.length > 3 ? `${head}…` : head || null;
  }
  if (typeof s.value === 'string') {
    return s.value.length > 60 ? `${s.value.slice(0, 60)}…` : s.value;
  }
  return null;
}

export const SECTIONS: SectionDef[] = [
  {
    path: '/',
    label: 'Dashboard',
    icon: BarChart3,
    keywords: ['home', 'overview', 'main'],
    group: 'workspace',
  },
  {
    path: '/projects',
    label: 'Projects',
    icon: FolderKanban,
    keywords: ['sites', 'list'],
    group: 'workspace',
  },
  {
    path: '/audits',
    label: 'Site audits',
    crumbLabel: 'Audits',
    icon: FileSearch,
    description: 'Technical SEO check — broken links, titles, speed, and fix list',
    keywords: ['crawl', 'technical', 'score'],
    group: 'research',
    detail: {
      routePath: '/audits/$auditId',
      paramName: 'auditId',
      queryKey: (id) => ['audit', id],
      entityLabel: (a) => {
        const date = shortDate(a.created_at);
        const score = typeof a.health_score === 'number' ? ` · score ${a.health_score}` : '';
        return date ? `Audit ${date}${score}` : null;
      },
      projectTab: 'audits',
    },
  },
  {
    path: '/keywords',
    label: 'Keywords',
    icon: KeyRound,
    description: 'Find terms worth targeting (volume, difficulty, clusters)',
    keywords: ['keyword research', 'runs', 'clusters', 'expansion'],
    group: 'research',
    detail: {
      routePath: '/keywords/$keywordRunId',
      paramName: 'keywordRunId',
      queryKey: (id) => ['keyword-run', id],
      entityLabel: (k) => keywordSeedLabel(k.seed),
      projectTab: 'keywords',
    },
  },
  {
    path: '/competitors',
    label: 'Competitors',
    icon: Globe,
    description: 'See who ranks and what content gaps to beat',
    keywords: ['scan', 'sitemap', 'rivals'],
    group: 'research',
    detail: {
      routePath: '/competitors/$runId',
      paramName: 'runId',
      queryKey: (id) => ['competitor-run', id],
      entityLabel: (c) => {
        const date = shortDate(c.created_at);
        const n = Array.isArray(c.competitor_ids) ? c.competitor_ids.length : null;
        if (!date) return null;
        return n ? `Scan ${date} · ${n} domain${n === 1 ? '' : 's'}` : `Scan ${date}`;
      },
      projectTab: 'competitors',
    },
  },
  {
    path: '/competitor-analyses',
    label: 'Content analysis',
    icon: ScanSearch,
    description: 'Tear down what ranks for a keyword and where you can beat it',
    keywords: ['teardown', 'serp', 'research', 'gaps'],
    group: 'research',
    sidebar: false,
    detail: {
      routePath: '/competitor-analyses/$analysisId',
      paramName: 'analysisId',
      queryKey: (id) => ['competitor-analysis', id],
      entityLabel: (a) => str(a.keyword),
      projectTab: 'competitors',
    },
  },
  {
    path: '/briefs',
    label: 'Briefs',
    icon: Notebook,
    description: 'Writer-ready outline, titles, and gaps for one keyword',
    keywords: ['content briefs', 'outline', 'meta', 'writeup'],
    group: 'content',
    detail: {
      routePath: '/briefs/$briefId',
      paramName: 'briefId',
      queryKey: (id) => ['brief', id],
      entityLabel: (b) => str(b.target_keyword),
      projectTab: 'briefs',
    },
  },
  {
    path: '/off-page-briefs',
    label: 'Off-Page Brief',
    icon: Megaphone,
    description:
      'Ready-to-post variants for Reddit, LinkedIn, Medium, Quora, and more',
    keywords: ['off page', 'reddit', 'linkedin', 'variants'],
    group: 'content',
    featureFlag: 'a10_off_page_brief',
    detail: {
      routePath: '/off-page-briefs/$offPageBriefId',
      paramName: 'offPageBriefId',
      // NB: matches use-off-page-brief.ts exactly — the old breadcrumb
      // registry pointed at ['off-page-brief', id] and never resolved.
      queryKey: (id) => ['off-page-briefs', 'detail', id],
      entityLabel: (b) => str(b.target_keyword) ?? str(b.title),
      projectTab: 'briefs',
    },
  },
  {
    path: '/tracking',
    label: 'Tracking',
    icon: Radar,
    description:
      'Keyword positions + Search Console — see if your SEO work moved rankings',
    keywords: ['ranks', 'positions', 'gsc', 'search console', 'measure'],
    group: 'strategy',
  },
  {
    path: '/calendars',
    label: 'Content calendar',
    crumbLabel: 'Calendars',
    icon: CalendarDays,
    description: 'Plan what to publish when — a month-by-month content schedule',
    keywords: ['plan', 'pillar', 'spoke'],
    group: 'strategy',
    detail: {
      routePath: '/calendars/$calendarId',
      paramName: 'calendarId',
      queryKey: (id) => ['calendar', id],
      entityLabel: (c) => {
        const date = shortDate(c.created_at);
        return date ? `Content plan ${date}` : null;
      },
      projectTab: 'calendars',
    },
  },
  {
    path: '/campaigns',
    label: 'Campaigns',
    icon: Target,
    description: 'Group SEO work around a goal (e.g. “win CRM keywords this quarter”)',
    keywords: ['orchestrator', 'autonomous', 'goal', 'auto-pilot'],
    group: 'strategy',
    detail: {
      routePath: '/campaigns/$campaignId',
      paramName: 'campaignId',
      queryKey: (id) => ['campaign', id],
      entityLabel: (c) => {
        const goal = str(c.goal);
        return goal ? (goal.length > 60 ? `${goal.slice(0, 60)}…` : goal) : null;
      },
      projectTab: 'campaigns',
    },
  },
  {
    path: '/forecasts',
    label: 'Forecasts',
    icon: TrendingUp,
    description: 'Estimate traffic upside if you hit ranking goals (planning, not proof)',
    keywords: ['predictive', 'scenarios', 'projection'],
    group: 'strategy',
    detail: {
      routePath: '/forecasts/$forecastId',
      paramName: 'forecastId',
      queryKey: (id) => ['forecast', id],
      entityLabel: (f) =>
        typeof f.horizon_days === 'number' ? `${f.horizon_days}-day forecast` : null,
      projectTab: 'forecasts',
    },
  },
  {
    path: '/reports',
    label: 'Reports',
    icon: LineChart,
    description: 'Period summary of traffic, rankings, and what changed',
    keywords: ['traffic', 'rankings', 'performance'],
    group: 'strategy',
    detail: {
      routePath: '/reports/$reportId',
      paramName: 'reportId',
      queryKey: (id) => ['report', id],
      entityLabel: (r) => {
        const cadence = str(r.cadence);
        const start = str(r.period_start);
        const end = str(r.period_end);
        if (cadence && start && end) return `${cadence} · ${start} → ${end}`;
        return cadence ? `${cadence} report` : null;
      },
      projectTab: 'reports',
    },
  },
];

/** Projects gets detail metadata too (name lives in the project cache). */
export const PROJECT_DETAIL: DetailDef = {
  routePath: '/projects/$projectId',
  paramName: 'projectId',
  queryKey: (id) => ['project', id],
  entityLabel: (p) => str(p.name),
};

export interface SettingsItemDef {
  path: string;
  label: string;
  icon: LucideIcon;
  keywords?: string[];
}

export const SETTINGS_ITEMS: SettingsItemDef[] = [
  { path: '/settings/account', label: 'Account', icon: UserCircle, keywords: ['profile', 'password', 'me'] },
  { path: '/settings/members', label: 'Members', icon: Users, keywords: ['team', 'roles', 'invite'] },
  { path: '/settings/billing', label: 'Billing', icon: CreditCard, keywords: ['plan', 'invoice', 'subscription', 'upgrade'] },
  { path: '/settings/integrations', label: 'Integrations', icon: Plug, keywords: ['gsc', 'analytics', 'connect', 'google'] },
];

/* ------------------------------------------------------------------ */
/* Nested detail routes (deeper than /section/<id>)                    */
/* ------------------------------------------------------------------ */

export interface NestedDetailDef {
  /** Matches a pathname; returns the entity id or null. */
  match: (pathname: string) => string | null;
  /** Section whose hierarchy this route belongs to. */
  parentPath: string;
  /** Static label prefix, e.g. "Auto-Pilot run". */
  label: string;
  routePath: string;
  paramName: string;
  queryKey: (id: string) => readonly unknown[];
  entityLabel: (entity: CachedEntity) => string | null;
}

export const NESTED_DETAILS: NestedDetailDef[] = [
  {
    match: (pathname) => {
      const parts = pathname.split('/').filter(Boolean);
      if (
        parts.length === 4 &&
        parts[0] === 'campaigns' &&
        parts[1] === 'auto' &&
        parts[2] === 'fires' &&
        UUID_RE.test(parts[3]!)
      ) {
        return parts[3]!;
      }
      return null;
    },
    parentPath: '/campaigns',
    label: 'Auto-Pilot run',
    routePath: '/campaigns/auto/fires/$fireId',
    paramName: 'fireId',
    // Auto fires ARE campaign rows; the fire page caches via useCampaign.
    queryKey: (id) => ['campaign', id],
    entityLabel: (c) => {
      const date = shortDate(c.created_at);
      return date ? `Auto-Pilot run · ${date}` : null;
    },
  },
];

/* ------------------------------------------------------------------ */
/* Lookup helpers                                                      */
/* ------------------------------------------------------------------ */

const sectionsByPath = new Map(SECTIONS.map((s) => [s.path, s]));

export function sectionByPath(path: string): SectionDef | undefined {
  return sectionsByPath.get(path);
}

export function crumbLabelFor(section: SectionDef): string {
  return section.crumbLabel ?? section.label;
}

/** Top-level paths that may act as a journey-trail root. */
export function isSectionRootPath(path: string): boolean {
  return (
    sectionsByPath.has(path) ||
    path === '/settings' ||
    SETTINGS_ITEMS.some((s) => s.path === path)
  );
}

export interface DetailMatch {
  kind: 'section' | 'project' | 'nested';
  /** Owning section path (`/audits`, `/campaigns`, …) or `/projects`. */
  sectionPath: string;
  sectionLabel: string;
  entityId: string;
  routePath: string;
  paramName: string;
  queryKey: readonly unknown[];
  entityLabel: (entity: CachedEntity) => string | null;
  projectTab?: string;
  /** Static label for nested routes (e.g. "Auto-Pilot run"). */
  staticLabel?: string;
}

/**
 * Classify a pathname as a detail route. Handles `/section/<uuid>`,
 * `/projects/<uuid>`, and registered nested routes (Auto-Pilot fires).
 */
export function matchDetail(pathname: string): DetailMatch | null {
  for (const nested of NESTED_DETAILS) {
    const id = nested.match(pathname);
    if (id) {
      const parent = sectionsByPath.get(nested.parentPath);
      return {
        kind: 'nested',
        sectionPath: nested.parentPath,
        sectionLabel: parent ? crumbLabelFor(parent) : nested.parentPath,
        entityId: id,
        routePath: nested.routePath,
        paramName: nested.paramName,
        queryKey: nested.queryKey(id),
        entityLabel: nested.entityLabel,
        staticLabel: nested.label,
      };
    }
  }

  const parts = pathname.split('/').filter(Boolean);
  if (parts.length !== 2 || !UUID_RE.test(parts[1]!)) return null;
  const [head, id] = parts as [string, string];

  if (head === 'projects') {
    return {
      kind: 'project',
      sectionPath: '/projects',
      sectionLabel: 'Projects',
      entityId: id,
      routePath: PROJECT_DETAIL.routePath,
      paramName: PROJECT_DETAIL.paramName,
      queryKey: PROJECT_DETAIL.queryKey(id),
      entityLabel: PROJECT_DETAIL.entityLabel,
    };
  }

  const section = sectionsByPath.get(`/${head}`);
  if (!section?.detail) return null;
  return {
    kind: 'section',
    sectionPath: section.path,
    sectionLabel: crumbLabelFor(section),
    entityId: id,
    routePath: section.detail.routePath,
    paramName: section.detail.paramName,
    queryKey: section.detail.queryKey(id),
    entityLabel: section.detail.entityLabel,
    projectTab: section.detail.projectTab,
  };
}

/**
 * Section owning a given project-page tab hash (e.g. `audits` → Site audits).
 * Used by journey breadcrumbs to render the tab level the user walked
 * through. Hashes without a detail-bearing section (overview, tracking,
 * integrations, settings) return undefined and the level is skipped.
 */
export function sectionByProjectTab(tabHash: string): SectionDef | undefined {
  return SECTIONS.find((s) => s.detail?.projectTab === tabHash);
}

/**
 * Query-key heads the breadcrumb may read (entity + project caches). The
 * crumbs hook subscribes to cache updates for these so entity names fill in
 * reactively as their queries resolve.
 */
export const CRUMB_QUERY_HEADS: ReadonlySet<string> = new Set([
  'project',
  ...SECTIONS.flatMap((s) =>
    s.detail ? [String(s.detail.queryKey('x')[0])] : [],
  ),
  ...NESTED_DETAILS.map((n) => String(n.queryKey('x')[0])),
]);

/**
 * Label for an arbitrary pathname without entity context — used for trail
 * entries and titles. Detail routes get their section label; unknown paths
 * fall back to the raw segment.
 */
export function sectionLabelForPath(pathname: string): string {
  if (pathname === '/') return 'Dashboard';
  const settings = SETTINGS_ITEMS.find((s) => s.path === pathname);
  if (settings) return settings.label;
  if (pathname === '/settings') return 'Settings';
  const section = sectionsByPath.get(pathname);
  if (section) return crumbLabelFor(section);
  const detail = matchDetail(pathname);
  if (detail) return detail.staticLabel ?? detail.sectionLabel;
  return pathname.split('/').filter(Boolean)[0] ?? pathname;
}
